import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { DEAL_STAGES, SUBSCRIPTION_PLANS, SERVICE_GROUPS } from "@/lib/dealStages";
import { usePipelineOptions } from "@/lib/pipelineOptions";
import { useAuth } from "@/lib/auth";
import { UserPlus, Building2, User } from "lucide-react";

const inputCls = "rounded-xl h-11";
const selectCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none";

/**
 * Comprehensive New Customer form. Captures both deal (subscription/contract)
 * details and the primary contact details in one step.
 *
 * Props:
 *  open, onOpenChange, team=[], onCreated(deal), currentUserId
 */
export function NewCustomerDialog({ open, onOpenChange, team = [], onCreated }) {
  const { stages, plans, serviceGroups } = usePipelineOptions();
  const { admin } = useAuth();
  const defaultOwner = admin?.id || "";

  const [form, setForm] = useState({
    customer_name: "",
    subscription_plan: "essential",
    service_group: "",
    contract_value: "",
    stage: "new",
    owner_id: defaultOwner,
    expected_close_date: "",
    notes: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    contact_title: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Reset owner default whenever the dialog opens or user context changes
    if (open) setForm((s) => ({ ...s, owner_id: s.owner_id || defaultOwner }));
  }, [open, defaultOwner]);

  const reset = () =>
    setForm({
      customer_name: "",
      subscription_plan: "essential",
      service_group: "",
      contract_value: "",
      stage: "new",
      owner_id: defaultOwner,
      expected_close_date: "",
      notes: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      contact_title: "",
    });

  const close = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.customer_name.trim(),
        stage: form.stage,
        subscription_plan: form.subscription_plan,
        value_sgd: Number(form.contract_value) || 0,
        owner_id: form.owner_id || undefined,
        source: "manual",
      };
      if (form.expected_close_date) payload.expected_close_date = form.expected_close_date;
      if (form.notes.trim()) payload.notes = form.notes.trim();
      if (form.service_group) payload.service_group = form.service_group;
      if (form.contact_name.trim()) payload.contact_name = form.contact_name.trim();
      if (form.contact_email.trim()) payload.contact_email = form.contact_email.trim();
      if (form.contact_phone.trim()) payload.contact_phone = form.contact_phone.trim();
      if (form.contact_title.trim()) payload.contact_title = form.contact_title.trim();

      const { data } = await api.post("/admin/deals", payload);
      toast.success("Customer added to pipeline");
      onCreated?.(data);
      close(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="new-customer-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-brand-navy flex items-center gap-2">
            <UserPlus size={18} className="text-brand-orange" /> New Customer
          </DialogTitle>
          <DialogDescription>
            Add a customer directly into the pipeline. Contact details help your team follow up.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6 pt-2">
          {/* Section 1: Customer details */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-brand-navy">
              <Building2 size={14} />
              <h3 className="text-xs font-semibold uppercase tracking-[0.25em]">Customer</h3>
              <div className="flex-1 border-t border-brand-navy/10" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Customer Name" required>
                <Input
                  value={form.customer_name}
                  onChange={(e) => update("customer_name", e.target.value)}
                  placeholder="Acme Bakery"
                  required
                  className={inputCls}
                  data-testid="new-customer-name"
                />
              </FormField>
              <FormField label="Subscription Plan" required>
                <select
                  value={form.subscription_plan}
                  onChange={(e) => update("subscription_plan", e.target.value)}
                  className={selectCls}
                  data-testid="new-customer-plan"
                >
                  {plans.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Contract Value (SGD)" required>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.contract_value}
                  onChange={(e) => update("contract_value", e.target.value)}
                  placeholder="0.00"
                  className={inputCls}
                  data-testid="new-customer-value"
                />
              </FormField>
              <FormField label="Status" required>
                <select
                  value={form.stage}
                  onChange={(e) => update("stage", e.target.value)}
                  className={selectCls}
                  data-testid="new-customer-stage"
                >
                  {stages.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Service Group">
                <select
                  value={form.service_group}
                  onChange={(e) => update("service_group", e.target.value)}
                  className={selectCls}
                  data-testid="new-customer-service-group"
                >
                  <option value="">— Optional —</option>
                  {serviceGroups.map((sg) => (
                    <option key={sg} value={sg}>{sg}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Person In Charge (PIC)" required>
                <select
                  value={form.owner_id}
                  onChange={(e) => update("owner_id", e.target.value)}
                  className={selectCls}
                  data-testid="new-customer-pic"
                >
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name || m.email}{m.id === admin?.id ? " (Me)" : ""}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Expected Close Date">
                <Input
                  type="date"
                  value={form.expected_close_date}
                  onChange={(e) => update("expected_close_date", e.target.value)}
                  className={inputCls}
                  data-testid="new-customer-close"
                />
              </FormField>
            </div>

            <FormField label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                placeholder="Anything the team should know…"
                className="rounded-xl w-full px-3 py-2 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none"
                data-testid="new-customer-notes"
              />
            </FormField>
          </section>

          {/* Section 2: Contact */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-brand-navy">
              <User size={14} />
              <h3 className="text-xs font-semibold uppercase tracking-[0.25em]">Primary Contact</h3>
              <div className="flex-1 border-t border-brand-navy/10" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Contact Name">
                <Input
                  value={form.contact_name}
                  onChange={(e) => update("contact_name", e.target.value)}
                  placeholder="Jane Smith"
                  className={inputCls}
                  data-testid="new-customer-contact-name"
                />
              </FormField>
              <FormField label="Contact Title">
                <Input
                  value={form.contact_title}
                  onChange={(e) => update("contact_title", e.target.value)}
                  placeholder="Head of Operations"
                  className={inputCls}
                  data-testid="new-customer-contact-title"
                />
              </FormField>
              <FormField label="Contact Email">
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => update("contact_email", e.target.value)}
                  placeholder="jane@acme.com"
                  className={inputCls}
                  data-testid="new-customer-contact-email"
                />
              </FormField>
              <FormField label="Contact Phone">
                <Input
                  value={form.contact_phone}
                  onChange={(e) => update("contact_phone", e.target.value)}
                  placeholder="+65 9123 4567"
                  className={inputCls}
                  data-testid="new-customer-contact-phone"
                />
              </FormField>
            </div>
          </section>

          <DialogFooter>
            <button
              type="button"
              onClick={() => close(false)}
              className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream rounded-full transition-colors"
              data-testid="new-customer-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.customer_name.trim()}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors disabled:opacity-60"
              data-testid="new-customer-submit"
            >
              {saving ? "Creating…" : "Create Customer"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm text-brand-navy">
        {label}{required && <span className="text-brand-orange ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
